/**
 * Générateur — Automatisme : savoir qu'une fraction est aussi le quotient :
 * 3/7 est le nombre qui, multiplié par 7, donne 3, donc 7 × 3/7 = 3.
 * Attendu ID : 1099 | Niveau : 4ème | Domaine : Nombres rationnels
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 9, ligne 655 :
 * « Savoir qu'une fraction est aussi le quotient : 3/7 (le nombre, qui,
 * multiplié par 7 donne 3, donc savoir que 7 × 3/7 = 3). »).
 *
 * ── Cadrage (sous-agent programme-maths, brouillon 2026-09-12) ─────────────
 * GEN NEUF COMPACT sur le geste 2 de 1049 seul (5e : « reconnaître
 * 3 × 7/3 = 7 sans multiplier »), le savoir déclaratif b × a/b = a, dans
 * les deux ordres (7 × 3/7 et 3/7 × 7, cycle 3 l. 980), avec le trou au
 * facteur entier (… × 3/7 = 3). C'est le socle de l'objectif l. 663 : la
 * mise en œuvre Eduscol 2026 prouve 2/3 × 5/7 par « 2/3 est le nombre qui,
 * multiplié par 3, donne 2 ». Pas un wrapper : la série flash de 1049 mêle
 * les deux gestes, et le geste 1 (7 × … = 3) est 1095.
 * Frontières : 7 × … = 3 (1095) ; 3 × 2/7 = 6/7 (un entier autre que le
 * dénominateur : 6e l. 1043 puis objectif l. 663) ; inverse (l. 665) ;
 * produit de fractions (l. 663). Entiers seulement.
 * MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 5NC16 absente
 * du dépôt, rien d'utilisable ; les cartes viennent du programme et de
 * l'item Eduscol 5e 2025 q.29 (« cinq fois trois cinquièmes » → 15, 5 ou 3).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre lignes KaTeX
 *     dans un ordre fixe, un entier à saisir : 7 × 3/7 = … ; 3/7 × 7 = … ;
 *     … × 3/7 = 3 (le facteur) ; 7 × 10/7 = … (une fraction plus grande que
 *     1 : le nombre qui, multiplié par 7, donne 10, c'est encore 10/7).
 *   Carte 2 « Le nombre qui, multiplié par… » (nombre_qcm_unique, nom de
 *     1049) : « Combien vaut 7 × 3/7 ? », QUATRE choix : 3 (clé), 7
 *     (nombre_recopie, la PRINCIPALE : q.29 « le nombre écrit deux fois »),
 *     3/7 (rien_fait), et 21 (produit) ou 7/3 (inversion) une fois sur deux.
 *     Avec a < b la clé est toujours deuxième par valeur (a/b < a < b < ab) :
 *     la fraction plus grande que 1, une fois sur quatre, déplace la clé.
 *   Carte 3 « Vrai ou faux » (sens_tableau_vf) : trois égalités tirées parmi
 *     sept gabarits, dont « 7 × 3/7 = 21/7 » qui est VRAIE (même valeur) ;
 *     chaque ligne est étiquetée par sa VALEUR (assert), une ou deux vraies.
 *
 * INVARIANTS : dénominateur b de 2 à 9, numérateur a de 1 à 20, a ≠ b, a/b
 * irréductible (sinon 6/4 = 3/2 et le facteur 4 ne serait plus « le »
 * dénominateur) ; fraction < 1 trois fois sur quatre ; quatre fractions
 * distinctes par série ; « produit_haut_bas » (21/49) a la même valeur que
 * rien_fait (3/7) : jamais les deux.
 * SACRIFIÉ (fiche d'aide à venir, « 7 parts de 3/7, ça fait 3 », l'image de
 * 1049) : (7 ; 3), l'exemple du programme, sous tous ses ordres et écritures
 * (7 × 3/7, 3/7 × 7, … × 3/7 = 3), auto-testé. Choix mode auto : À ARBITRER
 * contre l'import de (4 ; 13) de 1049 (13/4 > 1 alors que le programme
 * donne 3/7).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
    retirerCodesErreur,
} from '../../../utils';

const ATTENDU_ID = 1099;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Le nombre qui, multiplié par…';
const L_VF = 'Vrai ou faux';

const B_MIN = 2;
const B_MAX = 9;
const A_MAX = 20;

// ─── Le domaine ───────────────────────────────────────────────────────────

/** L'exemple du programme et de la fiche : b = 7, a = 3, sacrifié sous tous ses ordres. */
export const COUPLE_SACRIFIE = { a: 3, b: 7 };
const estSacrifie = (a, b) => a === COUPLE_SACRIFIE.a && b === COUPLE_SACRIFIE.b;

const gcd = (x, y) => (y === 0 ? x : gcd(y, x % y));

/** Un couple (a, b) : a/b irréductible, a ≠ b, b × a/b = a (assert). */
const verifier = (a, b) => {
    if (!Number.isInteger(a) || !Number.isInteger(b) || b < B_MIN || b > B_MAX || a < 1 || a > A_MAX || a === b || gcd(a, b) !== 1) {
        throw new Error(`gen_1099 : couple hors domaine ${a}/${b}`);
    }
    if (estSacrifie(a, b)) throw new Error('gen_1099 : couple sacrifié tiré');
    if ((b * a) % b !== 0 || (b * a) / b !== a) throw new Error('gen_1099 : arithmétique');
    return { a, b };
};

/** `sup1` : fraction plus grande que 1 (a > b) ; sinon plus petite que 1. */
const tirer = (sup1 = false) => {
    for (let essai = 0; essai < 80; essai++) {
        const b = randInt(B_MIN, B_MAX);
        const a = sup1 ? randInt(b + 1, A_MAX) : randInt(1, b - 1);
        if (gcd(a, b) !== 1 || estSacrifie(a, b)) continue;
        return verifier(a, b);
    }
    return verifier(sup1 ? 11 : 2, 5);
};

const cle = (c) => `${c.a}/${c.b}`;

// ─── Écritures ────────────────────────────────────────────────────────────

const fracTex = (a, b) => `\\dfrac{${a}}{${b}}`;
/** « 7 × 3/7 » ou « 3/7 × 7 » selon l'ordre. */
const produitTex = (c, fractionDevant) => (fractionDevant ? `${fracTex(c.a, c.b)} \\times ${c.b}` : `${c.b} \\times ${fracTex(c.a, c.b)}`);

const EXPLICATIONS = {
    nombre_recopie: '7 × 3/7, ce n\'est pas 7 : 3/7 est le nombre qui, multiplié par 7, donne 3.',
    rien_fait: 'Il y a bien un calcul à faire : 7 fois le nombre qui, multiplié par 7, donne 3, ça donne 3.',
    produit: 'On ne multiplie pas 7 par 3 : 3/7 est déjà le nombre qui, multiplié par 7, donne 3.',
    inversion: 'Le nombre 3/7 multiplié par 7 donne 3, son numérateur, pas 7/3.',
    regle: 'Une fraction est le nombre qui, multiplié par son dénominateur, redonne son numérateur : 7 × 3/7 = 3, et 3/7 × 7 = 3.',
};

// ─── Codes d'erreur (contenu.erreurs, codes communs du catalogue) ─────────

/** Le code commun d'un code du gen ; nombre_recopie (le nombre écrit deux fois) est neuf. */
const CODE_COMMUN = { nombre_recopie: 'nombre_ecrit_deux_fois', rien_fait: 'fraction_inchangee', produit: 'produit_des_nombres_vus', inversion: 'inversion' };

/** Les écritures fausses de b × a/b, une par famille : b, a/b, a × b, b/a. */
const ecrituresFausses = (c) => [['nombre_recopie', String(c.b)], ['rien_fait', `${c.a}/${c.b}`], ['produit', String(c.a * c.b)], ['inversion', `${c.b}/${c.a}`]];

/** Le code commun d'une famille, si aucune autre famille n'écrit la même chose (a = 1 : a × b s'écrit b). */
const codeSur = (c, code) => {
    const familles = ecrituresFausses(c);
    const [, ecriture] = familles.find(([k]) => k === code);
    return familles.every(([k, e]) => e !== ecriture || k === code) ? CODE_COMMUN[code] : null;
};

/** Les erreurs d'un trou qui attend a dans b × a/b, saisies telles que l'élève les tape. */
const erreursResultat = (c, trou) => ecrituresFausses(c).map(([code, valeur]) => ({ code: codeSur(c, code), trou, valeur })).filter((e) => e.code);

// ─── Carte 1 : Série flash (4 lignes saisie, un entier) ───────────────────

const CONSIGNE_FLASH = `<p><b>Complète chaque égalité.</b> Rappel : ${tex('\\dfrac{a}{b}')} est le nombre qui, multiplié par ${tex('b')}, donne ${tex('a')}.</p>`;

const lignesFlash = ([c1, c2, c3, c4]) => [
    `$${produitTex(c1, false)} = {{${c1.a}}}$`,
    `$${produitTex(c2, true)} = {{${c2.a}}}$`,
    `${'$'}{{${c3.b}}} \\times ${fracTex(c3.a, c3.b)} = ${c3.a}$`,
    `$${produitTex(c4, false)} = {{${c4.a}}}$`,
];

const assemblerFlash = (couples) => {
    const payload = makeTexteTrou(lignesFlash(couples).join('\n'), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie' });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { couples };
    // trous 0, 1 et 3 : le résultat ; trou 2 (le facteur) sans erreur nommée
    payload.contenu = { ...payload.contenu, erreurs: [0, 1, 3].flatMap((trou) => erreursResultat(couples[trou], trou)) };
    return payload;
};

const genFlash = () => {
    for (let essai = 0; essai < 80; essai++) {
        const couples = [tirer(false), tirer(false), tirer(false), tirer(true)];
        if (new Set(couples.map(cle)).size < 4) continue;
        return assemblerFlash(couples);
    }
    return assemblerFlash([verifier(2, 5), verifier(5, 8), verifier(4, 9), verifier(11, 3)]);
};

// ─── Carte 2 : QCM « Combien vaut 7 × 3/7 ? » ─────────────────────────────

const genQcm = () => {
    for (let essai = 0; essai < 80; essai++) {
        const c = tirer(randInt(1, 4) === 1);
        const fractionDevant = randomChoice([true, false]);
        const bon = String(c.a);
        // b/a avec a = 1 s écrirait 5/1 : le produit prend la place
        const quatrieme = c.a === 1 ? { texte: String(c.a * c.b), code: 'produit', val: [c.a * c.b, 1] } : randomChoice([
            { texte: String(c.a * c.b), code: 'produit', val: [c.a * c.b, 1] },
            { texte: `$${fracTex(c.b, c.a)}$`, code: 'inversion', val: [c.b, c.a] },
        ]);
        const faux = [
            { texte: String(c.b), code: 'nombre_recopie', val: [c.b, 1] },
            { texte: `$${fracTex(c.a, c.b)}$`, code: 'rien_fait', val: [c.a, c.b] },
            quatrieme,
        ];
        // quatre valeurs deux à deux distinctes (b/a = a exigerait b = a², impossible en irréductible ; on vérifie quand même)
        const vals = [[c.a, 1], ...faux.map((f) => f.val)];
        if (vals.some((u, i) => vals.some((v, j) => j > i && u[0] * v[1] === v[0] * u[1]))) continue;
        const payload = makeQcm([bon, ...faux.map((f) => f.texte)], bon, `<p><b>Combien vaut ${tex(produitTex(c, fractionDevant))} ?</b></p>`, opts(L_QCM));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...c, fractionDevant, distracteurs: faux.map(({ code }) => ({ code })) };
        payload.contenu = { ...payload.contenu, erreurs: faux.map((x) => ({ code: codeSur(c, x.code), valeur: x.texte })).filter((e) => e.code) };
        return payload;
    }
    const c = verifier(2, 5);
    const payload = makeQcm(['2', '5', `$${fracTex(2, 5)}$`, '10'], '2', `<p><b>Combien vaut ${tex(produitTex(c, false))} ?</b></p>`, opts(L_QCM));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...c, fractionDevant: false, distracteurs: [{ code: 'nombre_recopie' }, { code: 'rien_fait' }, { code: 'produit' }] };
    payload.contenu = { ...payload.contenu, erreurs: [['5', 'nombre_recopie'], [`$${fracTex(2, 5)}$`, 'rien_fait'], ['10', 'produit']].map(([valeur, code]) => ({ code: codeSur(c, code), valeur })) };
    return payload;
};

// ─── Carte 3 : Vrai ou faux (3 égalités, étiquetées par leur valeur) ──────

/** Valeur exacte [num, den] du membre droit d'un gabarit. */
const GABARITS = {
    juste: (c) => ({ droite: String(c.a), vrai: true }),
    juste_fraction_devant: (c) => ({ droite: String(c.a), vrai: true, fractionDevant: true }),
    non_simplifie: (c) => ({ droite: fracTex(c.a * c.b, c.b), vrai: true }),
    nombre_recopie: (c) => ({ droite: String(c.b), vrai: false, code: 'nombre_recopie' }),
    rien_fait: (c) => ({ droite: fracTex(c.a, c.b), vrai: false, code: 'rien_fait' }),
    produit: (c) => ({ droite: String(c.a * c.b), vrai: false, code: 'produit' }),
    inversion: (c) => ({ droite: fracTex(c.b, c.a), vrai: false, code: 'inversion' }),
};

/** [num, den] d'une écriture « n » ou « \dfrac{n}{d} ». */
const valeur = (s) => {
    const m = String(s).match(/^\\dfrac\{(\d+)\}\{(\d+)\}$/);
    return m ? [Number(m[1]), Number(m[2])] : [Number(s), 1];
};

const genVf = () => {
    for (let essai = 0; essai < 80; essai++) {
        const noms = shuffle(Object.keys(GABARITS)).slice(0, 3);
        const couples = [tirer(false), tirer(false), tirer(randInt(1, 3) === 1)];
        if (new Set(couples.map(cle)).size < 3) continue;
        if (noms.some((nom, i) => nom === 'inversion' && couples[i].a === 1)) continue; // b/1 n est pas une fraction à montrer
        const items = noms.map((nom, i) => {
            const c = couples[i];
            const g = GABARITS[nom](c);
            const fractionDevant = g.fractionDevant ?? randomChoice([true, false]);
            return { nom, c, ...g, ligne: tex(`${produitTex(c, fractionDevant)} = ${g.droite}`) };
        });
        const nbVraies = items.filter((it) => it.vrai).length;
        if (nbVraies < 1 || nbVraies > 2) continue;
        items.forEach((it) => {
            const [n, d] = valeur(it.droite);
            if ((it.c.a * d === n) !== it.vrai) throw new Error(`gen_1099 : ligne mal étiquetée ${it.ligne}`);
        });
        const payload = makeVraiFaux(items.map((it) => it.ligne), items.map((it) => (it.vrai ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items: items.map(({ nom, c, vrai, code }) => ({ nom, ...c, vrai, ...(code ? { code } : {}) })) };
        payload.contenu = { ...payload.contenu, erreurs: items.filter((it) => !it.vrai).map((it) => ({ code: codeSur(it.c, it.code), ligne: it.ligne })).filter((e) => e.code) };
        return payload;
    }
    const couples = [verifier(2, 5), verifier(5, 8), verifier(4, 9)];
    const lignes = [tex(`5 \\times ${fracTex(2, 5)} = 2`), tex(`${fracTex(5, 8)} \\times 8 = 8`), tex(`9 \\times ${fracTex(4, 9)} = ${fracTex(4, 9)}`)];
    const payload = makeVraiFaux(lignes, [0, 1, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items: [{ nom: 'juste', ...couples[0], vrai: true }, { nom: 'nombre_recopie', ...couples[1], vrai: false, code: 'nombre_recopie' }, { nom: 'rien_fait', ...couples[2], vrai: false, code: 'rien_fait' }] };
    payload.contenu = { ...payload.contenu, erreurs: [{ code: codeSur(couples[1], 'nombre_recopie'), ligne: lignes[1] }, { code: codeSur(couples[2], 'rien_fait'), ligne: lignes[2] }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre choix de valeurs deux à deux distinctes ; la clé est le numérateur a',
        'produit_haut_bas (21/49) a la même valeur que rien_fait (3/7) : jamais les deux',
        'le couple sacrifié (7 ; 3) ne sort d\'aucune carte, sous aucun ordre',
    ],
    qcm_unique: {
        support: 'b × a/b ou a/b × b, a/b irréductible, b de 2 à 9, a de 1 à 20 ; fraction plus grande que 1 une fois sur quatre (déplace la clé, sinon toujours deuxième par valeur)',
        familles: {
            nombre_recopie: 'b : le nombre écrit deux fois (Eduscol 5e 2025 q.29 : « cinq fois trois cinquièmes » → 5), la principale',
            rien_fait: 'a/b : la fraction recopiée, rien multiplié (nom de 1049)',
            produit: 'a × b : « il y a un ×, je multiplie » (q.29 → 15), une fois sur deux',
            inversion: 'b/a, une fois sur deux',
        },
    },
    vf_tableau_vf: 'sept gabarits dont trois vrais (a, a avec la fraction devant, ab/b non simplifié : VRAI en valeur), chaque ligne étiquetée par sa valeur',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    nombre_qcm_unique: genQcm,
    sens_tableau_vf: genVf,
};

// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-06 (chantier arbres-4e) ; sert aux exercices complexes du groupe Acquis.
export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, etapes: 1, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'nombre', label: L_QCM, etapes: 1, formatsList: [{ key: 'nombre_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, etapes: 1, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre. Le choix reste proposé,
// il part sans nom.
const CODES_RETIRES = {
    nombre_qcm_unique: ['inversion', 'produit_des_nombres_vus'],
    sens_tableau_vf: ['produit_des_nombres_vus', 'inversion'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};
